import { useMemo, useState, type ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import type { LatLngTuple } from "leaflet";
import { Marker, Popup, Polyline } from "react-leaflet";
import { ErrorBanner } from "../components/ErrorBanner";
import { SearchInput } from "../components/SearchInput";
import { TrainMap } from "../components/TrainMap";
import { DirectionBadge, LiveBadge } from "../components/Badges";
import { useCatalog } from "../context/CatalogContext";
import { useApi } from "../hooks/useApi";
import { useGpsFeed } from "../hooks/useGpsFeed";
import {
  dash,
  directionLabel,
  formatCoord,
  formatInstant,
  formatLate,
  formatTime,
  matchesQuery,
  trainLabel,
} from "../api/format";
import { isValidGps } from "../api/ws";
import { endFlagIcon, startFlagIcon, stationCrossedIcon, stationOpenIcon, trainIcon } from "../mapIcons";
import type { Station, StationStop, TrainDetail } from "../api/types";

export function TrackTrain() {
  const { id } = useParams();
  const trainId = Number(id);
  const hasId = Number.isInteger(trainId) && trainId > 0;

  if (!hasId) return <TrackPicker />;
  return <TrackMap trainId={trainId} />;
}

function TrackPicker() {
  const { trains, loading, error, reload } = useCatalog();
  const [q, setQ] = useState("");
  const rows = useMemo(
    () => trains.filter((t) => matchesQuery(q, t.train_id, t.train_number, t.train_name, t.train_name_ur, t.direction)),
    [trains, q],
  );

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>Track train</h1>
          <p className="muted">Pick a train to draw its route and follow live GPS.</p>
        </div>
        <SearchInput value={q} onChange={setQ} placeholder="Search trains…" />
      </div>
      <ErrorBanner error={error} onRetry={reload} />
      {loading ? <p className="muted">Loading catalog…</p> : null}
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Train</th>
              <th>Dir</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((t) => (
              <tr key={t.train_id}>
                <td>
                  <Link to={`/trains/${t.train_id}`}>{trainLabel(t)}</Link>
                </td>
                <td>
                  <DirectionBadge direction={t.direction} />
                </td>
                <td>
                  <Link className="btn" to={`/map/${t.train_id}`}>
                    Track
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function hasCoords(s: StationStop): s is StationStop & { latitude: number; longitude: number } {
  return typeof s.latitude === "number" && typeof s.longitude === "number" && Number.isFinite(s.latitude) && Number.isFinite(s.longitude);
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

function stationLines(
  id: number | null | undefined,
  stops: StationStop[],
  stationsById: Map<number, Station>,
): { title: string; detail: string } {
  if (id == null) return { title: "—", detail: "" };
  const stop = stops.find((s) => s.station_id === id);
  const cat = stationsById.get(id);
  const title = stop?.station_name || cat?.station_name || `Station ${id}`;
  const detail = [
    stop?.station_name_ur || cat?.station_name_ur,
    stop?.city || cat?.city,
    `id ${id}`,
  ]
    .filter(Boolean)
    .join(" · ");
  return { title, detail };
}

function TrackMap({ trainId }: { trainId: number }) {
  const { data, error, loading, retry } = useApi<TrainDetail>(`/v1/trains/${trainId}?live=true`);
  const { stationsById, trainsById } = useCatalog();
  const seed = data?.live && isValidGps(data.live) ? data.live : null;
  const { positions, connected } = useGpsFeed({ kind: "train", train_id: trainId }, seed);
  const live = positions.get(trainId) ?? seed;
  const fix = isValidGps(live) ? live : null;

  const stops = useMemo(
    () => (data?.stations ? [...data.stations].sort((a, b) => a.order_number - b.order_number) : []),
    [data],
  );
  const mappedStops = useMemo(() => stops.filter(hasCoords), [stops]);
  const polyline = useMemo<LatLngTuple[]>(
    () => mappedStops.map((s) => [s.latitude, s.longitude]),
    [mappedStops],
  );
  const startStop = mappedStops[0] ?? null;
  const endStop = mappedStops.length > 1 ? mappedStops[mappedStops.length - 1]! : null;
  const firstStop = stops[0] ?? null;
  const lastStop = stops.length ? stops[stops.length - 1]! : null;

  const catalogTrain = trainsById.get(trainId);
  const title = data ? trainLabel(data) : catalogTrain ? trainLabel(catalogTrain) : `Train ${trainId}`;
  const opposite = data?.opposite_train_id ?? catalogTrain?.opposite_train_id ?? null;
  const prev = stationLines(fix?.prev_station_id, stops, stationsById);
  const next = stationLines(fix?.next_station_id, stops, stationsById);

  return (
    <div className="map-page">
      <div className="map-toolbar">
        <div>
          <Link to={`/trains/${trainId}`} className="muted">
            ← Schedule
          </Link>
          <h1>{title}</h1>
          <p className="muted">
            REST live once, then WebSocket. {connected ? "WS connected." : "WS connecting…"}
          </p>
        </div>
        <div className="actions">
          {data ? (
            <>
              <DirectionBadge direction={data.direction} />
              <LiveBadge live={Boolean(data.is_live)} />
            </>
          ) : null}
        </div>
      </div>
      <ErrorBanner error={error} onRetry={retry} />
      {loading ? <p className="muted pad">Loading train…</p> : null}
      <div className="track-layout">
        <aside className="track-side">
          <h2>Train</h2>
          <dl className="facts">
            <Fact label="Name">{data?.train_name || catalogTrain?.train_name || "—"}</Fact>
            <Fact label="Name (Urdu)">
              <span className="urdu">{dash(data?.train_name_ur || catalogTrain?.train_name_ur)}</span>
            </Fact>
            <Fact label="Train number">{dash(data?.train_number ?? catalogTrain?.train_number)}</Fact>
            <Fact label="Train id">{trainId}</Fact>
            <Fact label="Direction">{directionLabel(data?.direction || catalogTrain?.direction)}</Fact>
            <Fact label="Description">{dash(data?.description || catalogTrain?.description)}</Fact>
            <Fact label="Is live (GPS pinging)">{data?.is_live || catalogTrain?.is_live ? "yes" : "no"}</Fact>
            <Fact label="Opposite train">
              {opposite != null ? <Link to={`/map/${opposite}`}>{opposite}</Link> : "—"}
            </Fact>
            <Fact label="Origin">
              {firstStop ? (
                <>
                  <Link to={`/stations/${firstStop.station_id}`}>{firstStop.station_name}</Link>
                  <div className="muted">
                    {[firstStop.city, firstStop.station_name_ur, `id ${firstStop.station_id}`, formatTime(firstStop.departure_time || firstStop.arrival_time)]
                      .filter(Boolean)
                      .join(" · ")}
                  </div>
                </>
              ) : (
                "—"
              )}
            </Fact>
            <Fact label="Destination">
              {lastStop ? (
                <>
                  <Link to={`/stations/${lastStop.station_id}`}>{lastStop.station_name}</Link>
                  <div className="muted">
                    {[lastStop.city, lastStop.station_name_ur, `id ${lastStop.station_id}`, formatTime(lastStop.arrival_time || lastStop.departure_time)]
                      .filter(Boolean)
                      .join(" · ")}
                  </div>
                </>
              ) : (
                "—"
              )}
            </Fact>
            <Fact label="Stops">{stops.length || "—"}</Fact>
          </dl>

          <h2>Live GPS</h2>
          {fix ? (
            <dl className="facts">
              <Fact label="Latitude">{formatCoord(fix.latitude)}</Fact>
              <Fact label="Longitude">{formatCoord(fix.longitude)}</Fact>
              <Fact label="Speed">{dash(fix.speed_kmh)} km/h</Fact>
              <Fact label="Late by minutes">
                {dash(fix.late_by_minutes)}
                <div className="muted">{formatLate(fix.late_by_minutes)}</div>
              </Fact>
              <Fact label="Status">{dash(fix.status)}</Fact>
              <Fact label="Locomotive">{dash(fix.locomotive_number)}</Fact>
              <Fact label="Ride id">{dash(fix.ride_id)}</Fact>
              <Fact label="Service date">{dash(fix.service_date)}</Fact>
              <Fact label="GPS train id">{dash(fix.train_id)}</Fact>
              <Fact label="Prev station">
                {fix.prev_station_id != null ? (
                  <>
                    <Link to={`/stations/${fix.prev_station_id}`}>{prev.title}</Link>
                    {prev.detail ? <div className="muted">{prev.detail}</div> : null}
                  </>
                ) : (
                  "—"
                )}
              </Fact>
              <Fact label="Next station">
                {fix.next_station_id != null ? (
                  <>
                    <Link to={`/stations/${fix.next_station_id}`}>{next.title}</Link>
                    {next.detail ? <div className="muted">{next.detail}</div> : null}
                  </>
                ) : (
                  "—"
                )}
              </Fact>
              <Fact label="Next station ETA">{formatTime(fix.next_station_eta)}</Fact>
              <Fact label="GPS updated">{formatInstant(fix.gps_updated_at)}</Fact>
            </dl>
          ) : (
            <p className="banner banner-warn">no live position yet</p>
          )}
        </aside>
        <div className="map-frame">
          <TrainMap fitPositions={polyline}>
            {polyline.length >= 2 ? <Polyline positions={polyline} pathOptions={{ color: "#2563eb", weight: 4, opacity: 0.85 }} /> : null}
            {mappedStops.map((s) => (
              <Marker
                key={`${s.station_id}-${s.order_number}`}
                position={[s.latitude, s.longitude]}
                icon={s.is_crossed ? stationCrossedIcon : stationOpenIcon}
                zIndexOffset={100}
              >
                <Popup>
                  <strong>{s.station_name}</strong>
                  <div className="muted">{[s.city, s.station_name_ur].filter(Boolean).join(" · ")}</div>
                  <div>{s.is_crossed ? "Crossed" : "Not crossed"}</div>
                  <div className="muted">
                    {formatTime(s.arrival_time)} / {formatTime(s.departure_time)}
                    {s.crossed_date_time ? ` · ${s.crossed_date_time}` : ""}
                  </div>
                  <div>
                    <Link to={`/stations/${s.station_id}`}>Station detail</Link>
                  </div>
                </Popup>
              </Marker>
            ))}
            {startStop ? (
              <Marker position={[startStop.latitude, startStop.longitude]} icon={startFlagIcon} zIndexOffset={200}>
                <Popup>
                  <strong>Start</strong>
                  <div>
                    <Link to={`/stations/${startStop.station_id}`}>{startStop.station_name}</Link>
                  </div>
                  <div className="muted">{[startStop.city, formatTime(startStop.departure_time || startStop.arrival_time)].filter(Boolean).join(" · ")}</div>
                </Popup>
              </Marker>
            ) : null}
            {endStop ? (
              <Marker position={[endStop.latitude, endStop.longitude]} icon={endFlagIcon} zIndexOffset={200}>
                <Popup>
                  <strong>End</strong>
                  <div>
                    <Link to={`/stations/${endStop.station_id}`}>{endStop.station_name}</Link>
                  </div>
                  <div className="muted">{[endStop.city, formatTime(endStop.arrival_time || endStop.departure_time)].filter(Boolean).join(" · ")}</div>
                </Popup>
              </Marker>
            ) : null}
            {fix ? (
              <Marker position={[fix.latitude, fix.longitude]} icon={trainIcon} zIndexOffset={500}>
                <Popup>
                  <strong>{title}</strong>
                  <div>
                    {dash(fix.speed_kmh)} km/h · {formatLate(fix.late_by_minutes)}
                  </div>
                </Popup>
              </Marker>
            ) : null}
          </TrainMap>
        </div>
      </div>
    </div>
  );
}
